/**
 * The owner putting a bill right after the fact.
 *
 * Three things, all for admins only and all logged at the top severity: the
 * discount on any bill changed or taken off (the payment and the books follow
 * the corrected total), a paid bill's payment moved to the tender it was
 * really paid by, and a bill cancelled outright. Each asks for a reason
 * first, because the reason is what the audit trail is for.
 */
import { useState } from 'react';
import { formatPKR } from '@foodrun/shared';

import { Button } from '../../components/ui/Button.jsx';

const field = 'h-11 rounded-lg border border-rule bg-surface-3 px-3 text-sm text-ink focus:border-brand-400 focus:outline-none';

/** Change or remove the discount. `current` is the bill's discount as it stands, if any. */
export function AdminDiscount({ current, total, busy, error, onApply, onCancel }) {
  const [kind, setKind] = useState(current?.type ?? 'percentage');
  const [value, setValue] = useState(current ? String(current.value) : '');
  const [reason, setReason] = useState('');
  const typed = Number(value);
  const valid = Number.isFinite(typed) && typed > 0 && reason.trim().length > 0;

  return (
    <div className="no-print space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-ink">Correct the discount</h3>
        <p className="text-xs text-ink-3">
          The bill is {formatPKR(total)} now{current ? ` with ${current.type === 'percentage' ? `${current.value}%` : formatPKR(current.value)} off` : ' with no discount'}. If it was paid, the payment on record moves to the corrected total.
        </p>
      </div>
      <div className="flex gap-2">
        <select value={kind} onChange={(e) => setKind(e.target.value)} aria-label="Discount kind" className={field}>
          <option value="percentage">Percent</option>
          <option value="fixed">Rupees</option>
        </select>
        <input type="number" min="0" step="any" value={value} onChange={(e) => setValue(e.target.value)} aria-label="Discount value" placeholder={kind === 'percentage' ? '%' : 'Rs'} className={`${field} flex-1`} />
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason for the correction" placeholder="Why is this being corrected?" className={`${field} w-full`} />
      {error && (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      )}
      <div className="flex flex-wrap gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Back
        </Button>
        {current && (
          <Button variant="danger" disabled={!reason.trim() || busy} busy={busy} onClick={() => onApply({ discount: null, reason: reason.trim() })}>
            Remove discount
          </Button>
        )}
        <Button className="flex-1" disabled={!valid || busy} busy={busy} onClick={() => onApply({ discount: { type: kind, value: typed, reason: reason.trim() }, reason: reason.trim() })}>
          Set discount
        </Button>
      </div>
    </div>
  );
}

/** Cancel the bill, with a reason. */
export function AdminCancel({ orderNumber, busy, error, onConfirm, onCancel }) {
  const [reason, setReason] = useState('');
  return (
    <div className="no-print space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-ink">Cancel {orderNumber}</h3>
        <p className="text-xs text-ink-3">The bill comes off the day's sales and its payment off the takings, as if it had not happened. This is written to the audit log with your name.</p>
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason for cancelling" placeholder="Why is this bill being cancelled?" className={`${field} w-full`} autoFocus />
      {error && (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Back
        </Button>
        <Button variant="danger" className="flex-1" disabled={!reason.trim() || busy} busy={busy} onClick={() => onConfirm(reason.trim())}>
          Cancel this bill
        </Button>
      </div>
    </div>
  );
}

const TENDERS = [
  { value: 'cash', label: 'Cash' },
  { value: 'card', label: 'Card' },
  { value: 'online', label: 'Online' },
  { value: 'foodpanda', label: 'Foodpanda' },
];
const tenderName = (m) => TENDERS.find((t) => t.value === m)?.label ?? m;

/** Move the payment to another tender — cash, card, online or Foodpanda. `current` is how the bill stands. */
export function AdminPaymentMethod({ current, total, busy, error, onApply, onCancel }) {
  const [method, setMethod] = useState(TENDERS.find((t) => t.value !== current)?.value ?? 'card');
  const [reference, setReference] = useState('');
  const [reason, setReason] = useState('');
  const crossesTax = (method === 'card') !== (current === 'card');
  const toFoodpanda = method === 'foodpanda';
  const fromFoodpanda = current === 'foodpanda';
  const valid = method !== current && reason.trim().length > 0;
  const note = toFoodpanda
    ? ' It becomes a Foodpanda order: the payment comes off the record and the bill goes on the Foodpanda tab, owed until the payout.'
    : fromFoodpanda
      ? ' It comes off the Foodpanda tab and becomes a counter sale paid this way.'
      : '';

  return (
    <div className="no-print space-y-3">
      <div>
        <h3 className="text-sm font-semibold text-ink">Move the payment</h3>
        <p className="text-xs text-ink-3">
          {formatPKR(total)} is on record as paid by {tenderName(current)}. Pick what it was really paid by.{note}
          {crossesTax || toFoodpanda || fromFoodpanda ? ' The bill is taxed again as the till would have taxed it, so the total may change; the payment follows it.' : ''}
        </p>
      </div>
      <div className="flex gap-2">
        <select value={method} onChange={(e) => setMethod(e.target.value)} aria-label="Paid by" className={field}>
          {TENDERS.filter((t) => t.value !== current).map((t) => (
            <option key={t.value} value={t.value}>
              {t.label}
            </option>
          ))}
        </select>
        <input
          value={reference}
          onChange={(e) => setReference(e.target.value)}
          aria-label={toFoodpanda ? 'Foodpanda order number' : 'Slip or transfer number'}
          placeholder={toFoodpanda ? 'Foodpanda order number (optional)' : 'Slip or transfer number (optional)'}
          className={`${field} flex-1`}
        />
      </div>
      <input value={reason} onChange={(e) => setReason(e.target.value)} aria-label="Reason for moving the payment" placeholder="Why is this being corrected?" className={`${field} w-full`} />
      {error && (
        <p role="alert" className="text-sm text-bad">
          {error}
        </p>
      )}
      <div className="flex gap-2">
        <Button variant="secondary" onClick={onCancel}>
          Back
        </Button>
        <Button className="flex-1" disabled={!valid || busy} busy={busy} onClick={() => onApply({ method, reason: reason.trim(), ...(reference.trim() ? { reference: reference.trim() } : {}) })}>
          Move to {tenderName(method)}
        </Button>
      </div>
    </div>
  );
}
